import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslatePipe } from '@ngx-translate/core';

export interface PreviewDialogData {
  contentUrl: string; // Sarà il Base64 o un URL
  type: string;
  nomeFile?: string;
}
@Component({
  selector: 'siat-preview-file-dialog',
  template: `<h2 mat-dialog-title>
      {{ 'DIALOG_PREVIEW.preview' | translate }}
    </h2>

    <mat-dialog-content class="mat-typography">
      @if (isImage) {
        <img
          [src]="safeContentUrl"
          style="max-width: 100%; max-height: 70vh;"
          [alt]="'preview' + data"
        />
      } @else if (isPdf) {
        <iframe
          [src]="safeContentUrl"
          type="application/pdf"
          style="width: 100%; height: 70vh; border: none;"
        >
          <p>
            {{ 'DIALOG_PREVIEW.pdf_not_supported' | translate }}
            <a [href]="safeContentUrl" download="file.pdf">
              {{ 'GENERAL.download' | translate }} PDF</a
            >
          </p>
        </iframe>
      } @else {
        <p>
          {{ 'DIALOG_PREVIEW.not_available' | translate: { item: data.type } }}
        </p>
      }
    </mat-dialog-content>

    <mat-dialog-actions class="d-flex align-end">
      @if (hasFileName) {
        <button
          mat-icon-button
          [attr.aria-label]="'GENERAL.download' | translate"
          [matTooltip]="'GENERAL.download' | translate"
          (click)="download()"
        >
          <mat-icon class="material-icons-outlined">download</mat-icon>
        </button>
      }
      <button mat-button mat-dialog-close>Chiudi</button>
    </mat-dialog-actions>`,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    CommonModule,
    MatButtonModule,
    TranslatePipe,
    MatTooltipModule,
    MatIconModule,
  ],
})
export class PreviewFileDialogComponent {
  /**
   * The content URL of the file to preview.
   */
  safeContentUrl: SafeResourceUrl;

  /**
   * Indicates if the file is an image.
   */
  isImage: boolean;

  /**
   * Indicates if the file is a PDF.
   */
  isPdf: boolean;

  hasFileName: boolean;

  /**
   * The data passed to the dialog.
   */
  data = inject<PreviewDialogData>(MAT_DIALOG_DATA);

  /**
   * The sanitizer service to sanitize the content URL.
   */
  sanitizer = inject(DomSanitizer);

  constructor() {
    this.isImage = this.data.type.startsWith('image/');
    this.isPdf = this.data.type === 'application/pdf';
    this.hasFileName = this.data.nomeFile ? true : false;

    let contentUrl = this.data.contentUrl;

    // Controlla se l'URL è già formattato (http, blob, o data URI)
    const isFormattedUrl = /^(http|blob:|data:)/.test(contentUrl);

    // Se non è formattato e ci aspettiamo un'immagine o un PDF,
    // assumiamo sia Base64 puro e aggiungiamo il prefisso.
    if (!isFormattedUrl && (this.isImage || this.isPdf)) {
      contentUrl = `data:${this.data.type};base64,${contentUrl}`;
    }

    // Sanitizza l'URL (ora completo)
    this.safeContentUrl =
      this.sanitizer.bypassSecurityTrustResourceUrl(contentUrl);
  }

  download() {
    const link = document.createElement('a');
    link.href = this.data.contentUrl;
    link.download = this.data.nomeFile;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }
}
